import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, FormControlLabel } from '@material-ui/core';
import { Field } from 'formik';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import ElegantSwitch from '../../../../../Shared/ElegantSwitch';
import FormikMultiSelectPopover from './FormikMultiSelectPopover';
import { normalizeAssessmentData, normalizeResourceData } from './helpers';

const useStyles = makeStyles(theme => ({
  title: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  switchControl: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .MuiTypography-body1': {
      fontSize: '1rem',
      fontWeight: 400,
      marginLeft: theme.spacing(1),
    },
  },
  divider: {
    width: '100%',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  featuresContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: 1400,
    gap: theme.spacing(2),
  },
  features: {
    flex: '1 0 45%',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 350,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  hideFeatures: {
    display: 'none',
  },
  nestedOptions: {
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    opacity: 0.7,
    transition: 'opacity 0.3s',
    '&.enabled': {
      opacity: 1,
    },
  },
}));

const FeaturesSection = ({
  canDisableOrders,
  canDisableAllergiendResources,
  canEnableClaimsManagement,
  canEnableImmunotherapyReport,
  canDisableAssessments,
  canDisableResources,
  clearBackendError,
  backendErrors,
  surveysPayload,
  resourcesPayload,
  disabled = false,
}) => {
  const classes = useStyles();
  const { disabledFeatures } = useSelector(state => state.appSettings);
  const {
    en: { forms },
  } = useSelector(state => state.i18n);

  // Compute available data from props
  const availableAssessments = useMemo(() => {
    if (surveysPayload?.result && Array.isArray(surveysPayload.result)) {
      return normalizeAssessmentData(surveysPayload.result);
    }
    return [];
  }, [surveysPayload]);

  const availableResources = useMemo(() => {
    if (Array.isArray(resourcesPayload)) {
      return normalizeResourceData(resourcesPayload);
    }
    return [];
  }, [resourcesPayload]);

  const showAutomation = useMemo(() => {
    return !disabledFeatures.includes('account_automations');
  }, [disabledFeatures]);

  // Memoized field change handler
  const handleFieldChange = useCallback(
    (fieldName, value, form) => {
      form.setFieldValue(fieldName, value);
      if (clearBackendError) {
        clearBackendError(fieldName);
      }
    },
    [clearBackendError]
  );

  return (
    <div className={classes.featuresContainer}>
      {/* Column 1: Basic Orders/Allergiend Features */}
      {(canDisableOrders || canDisableAllergiendResources) && (
        <div className={classes.features}>
          {/* Enable Orders */}
          {canDisableOrders && (
            <>
              <Field name="accountPermissions.disableOrders" type="checkbox">
                {({ field, form }) => (
                  <FormControlLabel
                    control={
                      <ElegantSwitch
                        name={field.name}
                        checked={!field.value}
                        disabled={disabled}
                        onChange={(e, checked) => {
                          handleFieldChange(field.name, !checked, form);
                        }}
                      />
                    }
                    className={classes.switchControl}
                    label={forms.enableOrders}
                  />
                )}
              </Field>

              {/* Orders Automation (nested) */}
              <Field name="accountPermissions.disableOrdersAutomations" type="checkbox">
                {({ field, form }) => {
                  return (
                    <div className={cx({ enabled: !form.values.accountPermissions.disableOrders })}>
                      <FormControlLabel
                        control={
                          <ElegantSwitch
                            name={field.name}
                            checked={
                              form.values.accountPermissions.disableOrders ? false : !field.value
                            }
                            onChange={(e, checked) => {
                              handleFieldChange(field.name, !checked, form);
                            }}
                            disabled={disabled || form.values.accountPermissions.disableOrders}
                          />
                        }
                        className={classes.switchControl}
                        label={forms.disableOrdersAutomations}
                      />
                    </div>
                  );
                }}
              </Field>
            </>
          )}

          {/* Enable Allergiend Resources */}
          {canDisableAllergiendResources && (
            <Field name="accountPermissions.disableAllergiendResources" type="checkbox">
              {({ field, form }) => (
                <FormControlLabel
                  control={
                    <ElegantSwitch
                      name={field.name}
                      checked={!field.value}
                      disabled={disabled}
                      onChange={(e, checked) => {
                        handleFieldChange(field.name, !checked, form);
                      }}
                    />
                  }
                  className={classes.switchControl}
                  label={forms.enableAllergiEndResources}
                />
              )}
            </Field>
          )}
        </div>
      )}

      {/* Column 2: Advanced Features */}
      {(canEnableClaimsManagement || canEnableImmunotherapyReport) && (
        <div className={classes.features}>
          {/* Enable Immunotherapy Report */}
          {canEnableImmunotherapyReport && (
            <Field name="accountPermissions.enabletImmunotherapyReport" type="checkbox">
              {({ field, form }) => (
                <FormControlLabel
                  control={
                    <ElegantSwitch
                      name={field.name}
                      checked={field.value}
                      disabled={disabled}
                      onChange={(e, checked) => {
                        handleFieldChange(field.name, checked, form);
                      }}
                    />
                  }
                  className={classes.switchControl}
                  label={forms.enableImmunotherapyReport}
                />
              )}
            </Field>
          )}

          {/* Enable Claims Management */}
          {canEnableClaimsManagement && (
            <Field name="accountPermissions.enableClaimsManagement" type="checkbox">
              {({ field, form }) => (
                <FormControlLabel
                  control={
                    <ElegantSwitch
                      name={field.name}
                      checked={field.value}
                      disabled={disabled}
                      onChange={(e, checked) => {
                        handleFieldChange(field.name, checked, form);
                      }}
                    />
                  }
                  className={classes.switchControl}
                  label={forms.enableClaimsManagement}
                />
              )}
            </Field>
          )}

          {/* Enable IntellyChart */}
          {canEnableClaimsManagement && (
            <Field name="accountPermissions.enableIntellyChart" type="checkbox">
              {({ field, form }) => (
                <FormControlLabel
                  control={
                    <ElegantSwitch
                      name={field.name}
                      checked={
                        !form.values.accountPermissions.enableClaimsManagement ? false : field.value
                      }
                      onChange={(e, checked) => {
                        handleFieldChange(field.name, checked, form);
                      }}
                      disabled={disabled || !form.values.accountPermissions.enableClaimsManagement}
                    />
                  }
                  className={classes.switchControl}
                  label={forms.enableIntellyChart}
                />
              )}
            </Field>
          )}
        </div>
      )}

      {/* Column 3: Assessments */}
      {canDisableAssessments && (
        <div className={classes.features}>
          {/* Enable Assessments Toggle */}
          <Field name="accountPermissions.disableAssessments" type="checkbox">
            {({ field, form }) => (
              <FormControlLabel
                control={
                  <ElegantSwitch
                    name={field.name}
                    checked={!field.value}
                    disabled={disabled}
                    onChange={(e, checked) => {
                      handleFieldChange(field.name, !checked, form);
                    }}
                  />
                }
                className={classes.switchControl}
                label={forms.enableAssessments}
              />
            )}
          </Field>

          {/* Assessments Selector */}
          <Field name="accountPermissions.disableAssessments">
            {({ field }) => (
              <div
                style={{
                  opacity: field.value || disabled ? 0.5 : 1,
                  pointerEvents: field.value || disabled ? 'none' : 'auto',
                }}
              >
                <FormikMultiSelectPopover
                  name="allowedSurveys"
                  label="Allowed Assessments"
                  subtitle="Select which assessments are available for this account"
                  availableItems={availableAssessments}
                  showAutomation={showAutomation}
                  searchPlaceholder="Search assessments..."
                  dialogTitle="Select Allowed Assessments"
                  clearBackendError={clearBackendError}
                  backendError={backendErrors?.allowedSurveys}
                />
              </div>
            )}
          </Field>
        </div>
      )}

      {/* Column 4: Resources */}
      {canDisableResources && (
        <div className={classes.features}>
          {/* Enable Resources Toggle */}
          <Field name="accountPermissions.disableResourcesAll" type="checkbox">
            {({ field, form }) => (
              <FormControlLabel
                control={
                  <ElegantSwitch
                    name={field.name}
                    checked={!field.value}
                    disabled={disabled}
                    onChange={(e, checked) => {
                      handleFieldChange(field.name, !checked, form);
                    }}
                  />
                }
                className={classes.switchControl}
                label={forms.enableResources}
              />
            )}
          </Field>

          {/* Resources Selector */}
          <Field name="accountPermissions.disableResourcesAll">
            {({ field }) => (
              <div
                style={{
                  opacity: field.value || disabled ? 0.5 : 1,
                  pointerEvents: field.value || disabled ? 'none' : 'auto',
                }}
              >
                <FormikMultiSelectPopover
                  name="allowedResources"
                  label="Allowed Resources"
                  subtitle="Select which resources are available for this account"
                  availableItems={availableResources}
                  showAutomation={false} // Resources don't have automation
                  showSelectAll
                  searchPlaceholder="Search resources..."
                  dialogTitle="Select Allowed Resources"
                  clearBackendError={clearBackendError}
                  backendError={backendErrors?.allowedResources}
                />
              </div>
            )}
          </Field>
        </div>
      )}
    </div>
  );
};

FeaturesSection.propTypes = {
  canDisableOrders: PropTypes.bool,
  canDisableAllergiendResources: PropTypes.bool,
  canEnableClaimsManagement: PropTypes.bool,
  canEnableImmunotherapyReport: PropTypes.bool,
  canDisableAssessments: PropTypes.bool,
  canDisableResources: PropTypes.bool,
  clearBackendError: PropTypes.func,
  backendErrors: PropTypes.object,
  surveysPayload: PropTypes.shape({
    result: PropTypes.array,
  }),
  resourcesPayload: PropTypes.array,
  disabled: PropTypes.bool,
};

FeaturesSection.defaultProps = {
  canDisableOrders: false,
  canDisableAllergiendResources: false,
  canEnableClaimsManagement: false,
  canEnableImmunotherapyReport: false,
  canDisableAssessments: false,
  canDisableResources: false,
  clearBackendError: null,
  backendErrors: null,
  surveysPayload: null,
  resourcesPayload: null,
};

export default FeaturesSection;
